import logger from './logger';
import { supabase } from './supabase';

// ── Puzzles ───────────────────────────────────────────────────────────────────

/** Compute sequential puzzle number based on publish_date rank among published puzzles */
/**
 * Single initialisation fetch — replaces separate getTodaysPuzzle +
 * getRecentPuzzles + computePuzzleNumber calls on app load.
 * Fires 2 parallel queries instead of up to 4 sequential ones:
 *   1. Today's published puzzle + cells
 *   2. Recent published puzzles list (last 7 days) + fallback in one query
 * Returns { puzzle, recentPuzzles }
 */
export async function initPuzzleData() {
  const today = new Date().toISOString().split('T')[0];
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - 6);
  const cutoffStr = cutoff.toISOString().split('T')[0];

  // Fire all queries in parallel — including app settings
  const [todayResult, recentResult, fallbackResult, settingsResult] = await Promise.all([
    supabase
      .from('puzzles')
      .select('*, puzzle_cells(*)')
      .eq('publish_date', today)
      .eq('status', 'published')
      .maybeSingle(),
    supabase
      .from('puzzles')
      .select('id, title, publish_date')
      .eq('status', 'published')
      .gte('publish_date', cutoffStr)
      .lte('publish_date', today)
      .order('publish_date', { ascending: false }),
    supabase
      .from('puzzles')
      .select('*, puzzle_cells(*)')
      .eq('status', 'fallback')
      .maybeSingle(),
    supabase
      .from('app_settings')
      .select('key, value'),
  ]);

  const recentPuzzles = recentResult.data || [];

  let puzzle = null;
  if (todayResult.data) {
    // Compute puzzle number from already-fetched list — no extra query needed
    const num = recentPuzzles.findIndex(p => p.publish_date === today) === -1
      ? recentPuzzles.length
      : recentPuzzles.length;
    puzzle = { ...todayResult.data, puzzle_number: num };
  } else if (fallbackResult.data) {
    puzzle = { ...fallbackResult.data, isFallback: true, puzzle_number: null };
  }

  // Parse settings into a simple key→value object
  const settings = {};
  for (const { key, value } of (settingsResult.data || [])) {
    settings[key] = value;
  }

  return { puzzle, recentPuzzles, settings };
}

/** Get today's published puzzle — kept for backwards compat, uses initPuzzleData */
export async function getTodaysPuzzle() {
  const { puzzle } = await initPuzzleData();
  return puzzle;
}

/** Get a published puzzle for a specific date (for archive play) */
export async function getPuzzleByDate(dateStr) {
  const today = new Date().toISOString().split('T')[0];
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - 6);
  const cutoffStr = cutoff.toISOString().split('T')[0];

  if (dateStr > today || dateStr < cutoffStr) return null;

  // Fire both queries in parallel — need full puzzle + puzzle number
  const [puzzleResult, countResult] = await Promise.all([
    supabase
      .from('puzzles')
      .select('*, puzzle_cells(*)')
      .eq('publish_date', dateStr)
      .eq('status', 'published')
      .maybeSingle(),
    supabase
      .from('puzzles')
      .select('publish_date')
      .eq('status', 'published')
      .lte('publish_date', dateStr)
      .order('publish_date', { ascending: true }),
  ]);

  if (!puzzleResult.data) return null;
  return { ...puzzleResult.data, puzzle_number: countResult.data?.length ?? null };
}

/** Get the last 7 days of published puzzles (for archive picker) */
export async function getRecentPuzzles() {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - 6);
  const cutoffStr = cutoff.toISOString().split('T')[0];
  const today = new Date().toISOString().split('T')[0];

  const { data } = await supabase
    .from('puzzles')
    .select('id, title, publish_date')
    .eq('status', 'published')
    .gte('publish_date', cutoffStr)
    .lte('publish_date', today)
    .order('publish_date', { ascending: false });

  return data || [];
}

/** Get a puzzle by ID (for admin preview) */
export async function getPuzzleById(id) {
  const { data, error } = await supabase
    .from('puzzles')
    .select('*, puzzle_cells(*)')
    .eq('id', id)
    .single();
  if (error) throw error;
  return data;
}

/** Get all puzzles (admin) */
export async function getAllPuzzles() {
  const { data, error } = await supabase
    .from('puzzles')
    .select('id, title, publish_date, status, created_at')
    .order('publish_date', { ascending: false });
  if (error) throw error;
  return data;
}

/** Save a new puzzle (admin) */
export async function savePuzzle(puzzle) {
  const { data, error } = await supabase
    .from('puzzles')
    .insert(puzzle)
    .select()
    .single();
  if (error) throw error;
  return data;
}

/** Update a puzzle (admin) */
export async function updatePuzzle(id, updates) {
  const { data, error } = await supabase
    .from('puzzles')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

/** Save puzzle cells (valid answers + hints per cell) */
export async function savePuzzleCells(cells) {
  const { data, error } = await supabase
    .from('puzzle_cells')
    .insert(cells)
    .select();
  if (error) throw error;
  return data;
}

/** Update puzzle cells */
export async function updatePuzzleCells(puzzleId, cells) {
  // Delete existing cells then reinsert
  await supabase.from('puzzle_cells').delete().eq('puzzle_id', puzzleId);
  const { data, error } = await supabase
    .from('puzzle_cells')
    .insert(cells.map(c => ({ ...c, puzzle_id: puzzleId })))
    .select();
  if (error) throw error;
  return data;
}

// ── Scores ────────────────────────────────────────────────────────────────────

/** Submit a score — routes through serverless function for rate limiting and bot protection */
export async function submitScore({ puzzleId, userId, username, score, correctCount, guessesUsed, timeSeconds }) {
  const res = await fetch('/api/submit-score', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ puzzleId, userId, username, score, correctCount, guessesUsed, timeSeconds }),
  });
  if (!res.ok) {
    const data = await res.json();
    throw new Error(data.error || 'Score submission failed');
  }
  return res.json();
}

/** Get leaderboard for a puzzle */
export async function getLeaderboard(puzzleId, limit = 20) {
  const { data, error } = await supabase
    .from('scores')
    .select('username, score, correct_count, guesses_used, time_seconds, played_at')
    .eq('puzzle_id', puzzleId)
    .order('score', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data;
}

/** Get rarity stats for a puzzle cell */
export async function getCellRarity(puzzleId, row, col) {
  const { data, error } = await supabase
    .from('cell_answers')
    .select('answer, count')
    .eq('puzzle_id', puzzleId)
    .eq('row_index', row)
    .eq('col_index', col)
    .order('count', { ascending: false });
  if (error) return [];
  return data;
}

/** Record a cell answer for rarity tracking */
export async function recordCellAnswer(puzzleId, row, col, answer) {
  const { error } = await supabase.rpc('increment_cell_answer', {
    p_puzzle_id: puzzleId,
    p_row:       row,
    p_col:       col,
    p_answer:    answer.toLowerCase(),
  });
  if (error) logger.warn('Rarity tracking error:', error);
}

// ── Players ───────────────────────────────────────────────────────────────────

/** Search players in local database.
 *  Matches if ANY word in the query appears anywhere in the name,
 *  so "Beckham" finds "David Beckham" and "David" does too.
 *  Results are sorted: full-string matches first, then partial.
 */
export async function searchPlayersDB(query) {
  const trimmed = query.trim();
  if (!trimmed) return [];

  // Normalize query to match normalized_name column in DB
  // Mirrors normalize_player_name() postgres function
  const normalized = trimmed
    .toLowerCase()
    .replace(/[đĐ]/g, 'd')
    .replace(/[łŁ]/g, 'l')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9 ]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

  const tokens = normalized.split(' ').filter(Boolean);

  // Search normalized_name so "Mijatovic" finds "Mijatović"
  const results = await Promise.all(
    tokens.map(token =>
      supabase
        .from('players')
        .select('id, name, nationality, positions, api_football_id')
        .ilike('normalized_name', `%${token}%`)
        .limit(20)
        .then(({ data }) => data || [])
    )
  );

  // Deduplicate — full normalized match first, then partial
  const seen = new Set();
  const merged = [];

  for (const list of results)
    for (const p of list)
      if (!seen.has(p.id)) { seen.add(p.id); merged.push(p); }

  return merged.slice(0, 10);
}

/** Insert or update a player manually */
export async function insertPlayer(player) {
  const { data, error } = await supabase
    .from('players')
    .upsert(player, { onConflict: 'name' })
    .select()
    .single();
  if (error) throw error;
  return data;
}

/**
 * Auto-sync valid answers from a puzzle into the players table.
 * For each player name in each cell, upsert a row in players and
 * merge in the criteria (row label + col label + puzzle theme) we
 * know are true for that player.
 *
 * Called automatically when saving a puzzle in the admin panel.
 */
export async function syncPlayersFromPuzzle(puzzle, cells, rowLabels, colLabels) {
  const updates = [];

  for (const cell of cells) {
    const { row_index, col_index, valid_answers = [] } = cell;
    const row    = rowLabels[row_index] || {};
    const col    = colLabels[col_index] || {};
    const theme  = puzzle.description || puzzle.title || '';

    for (const name of valid_answers) {
      if (!name?.trim()) continue;

      // Build criteria object — store both freetext label and criteria_id if available
      // criteria_id links to the library; freetext label preserved for backwards compat
      const newCriteria = {};
      if (row.label) {
        const key = row.criteria_id ? `cid:${row.criteria_id}` : row.label;
        newCriteria[key] = row.label; // key → display label
      }
      if (col.label) {
        const key = col.criteria_id ? `cid:${col.criteria_id}` : col.label;
        newCriteria[key] = col.label;
      }
      if (theme) newCriteria[`theme:${theme}`] = theme;

      updates.push({ name: name.trim(), newCriteria });
    }
  }

  // Deduplicate by name, merging criteria
  const byName = {};
  for (const { name, newCriteria } of updates) {
    if (!byName[name]) byName[name] = {};
    Object.assign(byName[name], newCriteria);
  }

  // Fetch existing players to merge criteria rather than overwrite
  const names = Object.keys(byName);
  if (!names.length) return;

  const { data: existing } = await supabase
    .from('players')
    .select('name, criteria')
    .in('name', names);

  const existingMap = {};
  for (const p of (existing || [])) existingMap[p.name] = p.criteria || {};

  // Upsert each player with merged criteria
  const rows = names.map(name => ({
    name,
    criteria: { ...(existingMap[name] || {}), ...byName[name] },
  }));

  const { error } = await supabase
    .from('players')
    .upsert(rows, { onConflict: 'name' });

  if (error) logger.warn('Player sync error:', error);
}

// ── User profiles ─────────────────────────────────────────────────────────────

/** Get or create user profile */
export async function getOrCreateProfile(userId, email) {
  const { data: existing } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single();
  if (existing) return existing;

  const { data, error } = await supabase
    .from('profiles')
    .insert({ id: userId, email, username: email.split('@')[0], streak: 0 })
    .select()
    .single();
  if (error) throw error;
  return data;
}

/** Update streak */
export async function updateStreak(userId, streak) {
  const { error } = await supabase
    .from('profiles')
    .update({ streak, last_played: new Date().toISOString() })
    .eq('id', userId);
  if (error) logger.warn('Streak update error:', error);
}

// ── Criteria Library ──────────────────────────────────────────────────────────

/** Get all criteria ordered by usage then label */
export async function getAllCriteria() {
  const { data, error } = await supabase
    .from('criteria')
    .select('id, tag, label, context, category, usage_count')
    .order('usage_count', { ascending: false })
    .order('label', { ascending: true });
  if (error) throw error;
  return data || [];
}

/** Search criteria by text */
export async function searchCriteria(query) {
  const { data, error } = await supabase
    .from('criteria')
    .select('id, tag, label, context, category, usage_count')
    .or(`label.ilike.%${query}%,tag.ilike.%${query}%,context.ilike.%${query}%`)
    .order('usage_count', { ascending: false })
    .limit(20);
  if (error) throw error;
  return data || [];
}

/** Increment usage count when a criterion is used in a puzzle */
export async function incrementCriteriaUsage(id) {
  const { error } = await supabase.rpc('increment_criteria_usage', { criteria_id: id });
  if (error) logger.warn('Criteria usage increment failed:', error.message);
}

// ── App settings ──────────────────────────────────────────────────────────────

/** Get a single app setting by key */
export async function getAppSetting(key) {
  const { data } = await supabase
    .from('app_settings')
    .select('value')
    .eq('key', key)
    .maybeSingle();
  return data?.value ?? null;
}

/**
 * Auto-suggest difficulty based on average valid answer count per cell.
 * Thresholds are configurable — defaults match app_settings seeds.
 * easy:   avg > easyThreshold   (default > 5)
 * medium: avg > mediumThreshold (default > 3)
 * hard:   avg <= mediumThreshold
 */
export function suggestDifficulty(cells, easyThreshold = 5, mediumThreshold = 3) {
  if (!cells?.length) return 'medium';
  const counts = cells.map(c => c.valid_answers?.length || 0).filter(n => n > 0);
  if (!counts.length) return 'medium';
  const avg = counts.reduce((a, b) => a + b, 0) / counts.length;
  if (avg > easyThreshold)   return 'easy';
  if (avg > mediumThreshold) return 'medium';
  return 'hard';
}
